/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductImageStyles } from "@stoneage/Overrides/Components/ProductImage";
import { ProductCardSelections } from "@stoneage/Overrides/Widgets/ProductList/ProductCardSelections";
import ProductListActions, {
    ProductListActionsStyles,
} from "@stoneage/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@stoneage/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@stoneage/Overrides/Widgets/ProductList/ProductListProductInformation";
import { color } from "@stoneage/XDL/Utilities";
import { horizontalProductCardSharedStyles, partsNumberStyle } from "@stoneage/XDL/Utilities/SharedStyles/productCards";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
    };
};

const mapDispatchToProps = {};

type Props = ProductCardSelections &
    HasProduct & {
        relatedProductsEventHandler?: (destination: string) => void;
    } & ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListProductCardStyles {
    authContainer?: InjectableCss;
    NonAuthContainer?: InjectableCss;
    authenticatedPartNumbers?: InjectableCss;
    leftColumnWrapper?: InjectableCss;
    rightColumnWrapper?: InjectableCss;
    rightColumnWrapperContainer?: InjectableCss;
    infoWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    searchScoreDataGridItem?: InjectableCss;
    productInformationStyles?: ProductListProductInformationStyles;
    productActionsStyles?: ProductListActionsStyles;
    productImage: ProductImageStyles;
    partNumbersAuthenticated?: InjectableCss;
    partNumbersNonAuthenticated?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    authContainer: {
        css: css`
            display: flex;
            border-bottom: 1px solid ${color("common.ui6")};
            margin-bottom: 7px;
            flex: 1;
        `,
    },
    NonAuthContainer: {
        css: css`
            display: flex;
            padding-top: 10px;
            flex: 1;
        `,
    },
    authenticatedPartNumbers: {
        css: css`
            height: 40px;
            padding-bottom: 7px;
        `,
    },
    leftColumnWrapper: {
        css: css`
            width: 33%;
            padding-right: 20px;
            margin-bottom: 20px;
        `,
    },
    rightColumnWrapper: {
        css: css`
            display: flex;
            width: 66%;
            flex: 1;
        `,
    },
    infoWrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    actionsWrapper: {
        css: css`
            width: 100%;
        `,
    },
    ...horizontalProductCardSharedStyles,
    productInformationStyles: {
        ...horizontalProductCardSharedStyles.productInformationStyles,
        container: {
            css: css`
                width: 100%;
                display: flex;
                flex-direction: column;
                flex: 1;
                justify-content: space-between;
            `,
        },
        productAttributes: {
            ...horizontalProductCardSharedStyles.productInformationStyles.productAttributes,
            attributeWrapper: {
                css: css`
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    white-space: nowrap;
                `,
            },
        },
    },
};

const styles = productCardStyles;

const ProductCardNarrow: FC<Props> = ({
    product,
    isAuthenticated,
    showImage,
    showCompare,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showPrice,
    showAddToCart,
    relatedProductsEventHandler,
}) => {
    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-direction: column;
                width: 100%;
                justify-content: space-between;
            `}
            className="narrow-wrapper"
        >
            <StyledWrapper
                {...(isAuthenticated ? styles.authContainer : styles.NonAuthContainer)}
                className="product-card-narrow-cont"
            >
                <StyledWrapper {...styles.leftColumnWrapper} className="image-cont">
                    <ProductListProductImage showImage={showImage} extendedStyles={styles.productImage} imageClickHandler={relatedProductsEventHandler} />
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumnWrapper} className="right-column-cont">
                    <StyledWrapper {...styles.infoWrapper} className="info-wrapper">
                        <ProductListProductInformation
                            showTitle={showTitle}
                            showPartNumbers={showPartNumbers}
                            showAvailability={showAvailability}
                            showAttributes={showAttributes}
                            extendedStyles={{
                                ...styles.productInformationStyles,
                                partNumbersWrapper: {
                                    css: css`
                                        ${partsNumberStyle}
                                        border-top: 1px solid ${color("common.ui6")};
                                        padding-bottom: 7px;
                                        margin-top: auto;
                                    `,
                                },
                            }}
                            view="Grid"
                            productDescriptionClickHandler={relatedProductsEventHandler}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper} className="actions-wrapper">
                <ProductListActions
                    showPrice={showPrice}
                    showCompare={showCompare}
                    showAvailability={showAvailability}
                    showAddToCart={showAddToCart}
                    extendedStyles={styles.productActionsStyles}
                    ctaHandler={relatedProductsEventHandler}
                />
                <StyledWrapper {...styles.searchScoreDataGridItem}>
                    <ProductSearchScoreData />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductCardNarrow));
